// Customer-facing copy for every pricing-failure mode the portal can surface.
// The portal OWNS the wording (its voice) — but it makes NO pricability
// decision: it renders whatever Nexus decides (a price, or a
// CANNOT_PRICE/INVALID_CONFIG outcome). It never decides a job can't be priced
// and never hardcodes a pricing number (see docs/ADR-013). The ERP's own
// `message` is kept for diagnostics — logged, never rendered.
//
// Deliberately narrow reason_code use: EVERY not-auto-priceable outcome renders
// ONE generic contact-sales handoff, with no price and no Nexus number
// (min/max quantity are stripped upstream and never rendered — sales softens a
// minimum in person). The SINGLE reason_code the portal reads for differentiated
// copy is 'specialty_finishing_review' (a warm "our team will follow up"). See
// docs/nexus-handoff-pricability-ownership.md.
//
// One headline + body + optional reference id per failure. Every branch ends
// with a clear path forward (email/call sales).
//
// i18n: this is a pure module (no React component), so it uses the i18next
// singleton directly rather than the useTranslation hook. All strings live
// under the 'pricing' namespace, key path 'copy.<case>.headline'/'body' — the
// singleton's default namespace is 'common', so every call fully-prefixes
// with 'pricing:'.

import i18n from 'i18next';
import type { PricingFailureDto, SalesRepDto } from 'shared';

export const SUPPORT_EMAIL = 'sales@argoshipping.com';
export const SUPPORT_PHONE = '815-331-6000';

// Who the customer is told to contact when NOTHING named a rep for them — an
// anonymous or never-priced quote, a transport failure that never reached the
// engine, the release-v1 pricing lockdown. Nexus falls back to this exact pair
// itself when an account has neither a Partner Success Rep nor a Sales Rep
// assigned, so the two sides agree and every message reads the same either way:
// "Argo Sales would be glad to quote it for you. Email sales@argoshipping.com".
//
// This is why no copy branch below tests whether a rep is a real person. One
// template covers both, and the wording never has to survive a null name.
export const DEFAULT_SALES_REP: SalesRepDto = { name: 'Argo Sales', email: SUPPORT_EMAIL };

// Nexus rides `sales_rep` on every response its pricing engine produces — the
// priced 200 and each 422 alike — resolving it from the settling company (that
// account's Partner Success Rep first, else its Sales Rep). A partner with a
// named rep must be pointed at THAT person rather than the shared inbox; that is
// the whole of Nexus feedback FB-0026. Absent only where no engine response
// exists (see PricingFailureDto.sales_rep), which is what the default covers.
//
// There is deliberately no phone half: Nexus holds no publishable work number
// per rep, so every message pairs the rep's own address with the portal's main
// line (SUPPORT_PHONE). Never synthesize a per-rep number.
export function salesRepFor(failure: PricingFailureDto | null): SalesRepDto {
  return failure?.sales_rep ?? DEFAULT_SALES_REP;
}

// "Quote now, art to follow" disclaimer, shown near the price when the quote is
// flagged artwork_pending (the customer quoted before final artwork was in hand).
// Nexus OWNS the canonical wording (constants/quoteDisclaimers.ARTWORK_PENDING_
// DISCLAIMER) and returns it as `artwork_disclaimer` on the quote-status feed;
// this constant mirrors it VERBATIM so the portal can render the disclaimer on
// its own pre-order price surfaces (the live "See my price" explorer + the saved
// snapshot) without a status round-trip. Keep the two strings identical — if
// Nexus changes the wording, update this to match.
// NOT translated here: this is a verbatim mirror of Nexus-owned copy (Nexus
// Phase 4 localizes it on its side); the portal must not re-translate copy it
// doesn't author. Stays English pending Nexus localization.
export const ARTWORK_PENDING_DISCLAIMER =
  'Pricing is subject to change upon receipt of final artwork.';

export type CustomerFailureCopy = {
  headline: string;
  body: string;
  // The contact this message names — already folded into `body`, and carried
  // separately so the renderer can build the mailto and label the link with the
  // same person the sentence just introduced.
  sales_rep: SalesRepDto;
  // Hint to the renderer that a click-to-email link belongs in the
  // banner. True for every code today, but explicit so the contract is
  // clear if we ever add a fully-self-service failure.
  show_contact_sales: boolean;
  // If present, render a "Reference: <id>" footnote so the customer can
  // quote it to sales for fast lookup against the audit log.
  reference_id?: string;
  // Optional retry hint surfaced when the failure is transient.
  retry_after_seconds?: number;
};

// `failure === null` means a transport-level failure where the SPA
// didn't even get a structured DTO back (portal-backend itself
// unreachable). Treat it the same as PRICING_UNAVAILABLE.
export function customerFacingFailure(
  failure: PricingFailureDto | null,
): CustomerFailureCopy {
  // Resolved ONCE for every branch below, including the null-failure one: no
  // message the portal writes may point at a different contact than the rep
  // Nexus named for this account.
  const rep = salesRepFor(failure);

  if (!failure) {
    return {
      headline: i18n.t('pricing:copy.unreachable.headline'),
      body: i18n.t('pricing:copy.unreachable.body', {
        name: rep.name,
        email: rep.email,
        phone: SUPPORT_PHONE,
      }),
      sales_rep: rep,
      show_contact_sales: true,
    };
  }

  const reference_id = failure.details?.reference_id;
  const retry_after_seconds = failure.details?.retry_after_seconds;

  switch (failure.code) {
    case 'CANNOT_PRICE': {
      // The portal makes NO pricability decision (ADR-013) and does NOT fan
      // Nexus's reason_code out into per-reason customer copy. Everything that
      // can't be auto-priced — a quantity below/above the priced range, a job
      // that doesn't clear a service minimum (the withheld-line qualification
      // block, collapsed to CANNOT_PRICE server-side), a margin/outsourced/
      // external outcome, or a no-rule-matched config — resolves to ONE
      // consistent contact-sales handoff. No price is shown and no Nexus number
      // (min/max quantity, thresholds) is rendered: sales softens the issue in
      // person. The ONE exception is specialty finishing, a warm "we'll follow
      // up" handoff Nexus explicitly tags + fires a sales task for.
      const reason = failure.details?.reason_code;

      // Specialty finishing (foil, emboss, spot UV, die-cut, PMS match, laser
      // etching) — Nexus quotes it by hand and fires the sales follow-up on this
      // path; the portal just reassures the customer in its own voice.
      if (reason === 'specialty_finishing_review') {
        return {
          headline: i18n.t('pricing:copy.specialtyFinishing.headline'),
          body: i18n.t('pricing:copy.specialtyFinishing.body', {
            name: rep.name,
            email: rep.email,
            phone: SUPPORT_PHONE,
          }),
          sales_rep: rep,
          show_contact_sales: true,
          reference_id,
        };
      }

      // Every other CANNOT_PRICE (incl. any other reason_code) — one generic,
      // outcome-agnostic contact-sales handoff. No price, no number.
      return {
        headline: i18n.t('pricing:copy.cannotPrice.headline'),
        body: i18n.t('pricing:copy.cannotPrice.body', {
          name: rep.name,
          email: rep.email,
          phone: SUPPORT_PHONE,
        }),
        sales_rep: rep,
        show_contact_sales: true,
        reference_id,
      };
    }

    case 'INVALID_CONFIG':
      // Most INVALID_CONFIGs carry `details.field_keys` and are
      // rendered inline next to the offending field; this branch is
      // for residual cases where the engine rejected the shape but we
      // could not map it to a specific field.
      return {
        headline: i18n.t('pricing:copy.invalidConfig.headline'),
        body: i18n.t('pricing:copy.invalidConfig.body', {
          name: rep.name,
          email: rep.email,
          phone: SUPPORT_PHONE,
        }),
        sales_rep: rep,
        show_contact_sales: true,
        reference_id,
      };

    case 'PRICING_UNAVAILABLE':
      return {
        headline: i18n.t('pricing:copy.pricingUnavailable.headline'),
        body: i18n.t('pricing:copy.pricingUnavailable.body', {
          name: rep.name,
          email: rep.email,
          phone: SUPPORT_PHONE,
        }),
        sales_rep: rep,
        show_contact_sales: true,
        reference_id,
        retry_after_seconds,
      };

    case 'CUSTOMER_NOT_CONFIGURED':
      // Newer ERP builds quietly fall back to the standard rate (signaled via
      // tier_applied.code === 'pending_shown_as_standard') so this code
      // mostly fires on legacy deployments. Kept here for completeness.
      return {
        headline: i18n.t('pricing:copy.customerNotConfigured.headline'),
        body: i18n.t('pricing:copy.customerNotConfigured.body', {
          name: rep.name,
          email: rep.email,
          phone: SUPPORT_PHONE,
        }),
        sales_rep: rep,
        show_contact_sales: true,
        reference_id,
      };

    case 'RATE_LIMITED':
      return {
        headline: i18n.t('pricing:copy.rateLimited.headline'),
        body: i18n.t('pricing:copy.rateLimited.body', {
          name: rep.name,
          email: rep.email,
          phone: SUPPORT_PHONE,
        }),
        sales_rep: rep,
        show_contact_sales: true,
        reference_id,
        retry_after_seconds,
      };

    case 'CREDIT_HOLD':
      // Account is on AR credit hold (Nexus emits this from both the pricing
      // and submit endpoints). Customer-safe handoff only — the internal
      // reason is stripped at the boundary and never shown here.
      return {
        headline: i18n.t('pricing:copy.creditHold.headline'),
        body: i18n.t('pricing:copy.creditHold.body', {
          name: rep.name,
          email: rep.email,
          phone: SUPPORT_PHONE,
        }),
        sales_rep: rep,
        show_contact_sales: true,
        reference_id,
      };

    default: {
      // Exhaustiveness check: if a new code is added to the shared
      // union, TypeScript flags this default branch. Defensive
      // fallback so we never strand the customer without a path.
      const _exhaustive: never = failure.code;
      void _exhaustive;
      return {
        headline: i18n.t('pricing:copy.snag.headline'),
        body: i18n.t('pricing:copy.snag.body', {
          name: rep.name,
          email: rep.email,
          phone: SUPPORT_PHONE,
        }),
        sales_rep: rep,
        show_contact_sales: true,
        reference_id,
      };
    }
  }
}
